import React, {
  useState, useCallback, useRef, useImperativeHandle, forwardRef, useMemo,
} from "react";
import { Table, DatePicker, Input } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Error from "../../Error";
import "./PcrPending.css";
import StatusTag from "../../Common/components/StatusTag";
import { getTestsDataForPcr } from "../../../actions/pcr";
import { formatAntdDateRange } from "../../../utils/helpers";

const { RangePicker } = DatePicker;

const defaultGbIdDateRange = () => {
  const endDate = dayjs().subtract(1, "day");
  const startDate = endDate.subtract(1, "month");
  return [startDate, endDate];
};

const tableColumns = [
  {
    title: "Workorder ID",
    dataIndex: "WorkorderId",
    key: "WorkorderId",
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
  {
    title: "Genista ID",
    dataIndex: "GenistaId",
    key: "GenistaId",
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
  {
    title: "Sample Type",
    dataIndex: "SampleType",
    key: "SampleType",
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
  {
    title: "Analysis",
    dataIndex: "Analysis",
    key: "Analysis",
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
  {
    title: "Result",
    dataIndex: "Result",
    key: "Result",
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "Status",
    render: (status) => <StatusTag type="TEST_STATUS" result={status} />,
    onCell: () => ({ className: "PcrPending__Table__Td" }),
  },
];

const defaultPageSize = 100;

const PcrPendingQueue = forwardRef((props, ref) => {
  const [genistaId, setGenistaId] = useState("");
  const [pendingData, setPendingData] = useState([]);
  const [showError, setShowError] = useState({ show: false, message: "" });
  const [loading, setLoading] = useState(false);
  const [gbIdDateRange, setGbIdDateRange] = useState(defaultGbIdDateRange());
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: defaultPageSize,
    pageSizeOptions: ["10", "50", "100", "200"],
  });

  const handlePageChange = useCallback((page, pageSize) => {
    setPaginationConfig((prevState) => ({ ...prevState, current: page, pageSize }));
  }, []);

  // Keeps loadPendingData's closure stable (empty deps) while always reading
  // the latest gbIdDateRange. Callers with a brand-new range (fetchPendingRecords,
  // handleDateRangeChange) pass it explicitly via rangeOverride so the API call uses
  // the correct value before the next render commits.
  const gbIdDateRangeRef = useRef(gbIdDateRange);
  gbIdDateRangeRef.current = gbIdDateRange;

  const loadPendingData = useCallback(async (searchGenistaId, rangeOverride) => {
    const range = rangeOverride ?? gbIdDateRangeRef.current;
    if (!range || range.length < 2) {
      toast.warning("Please select a date range.");
      return;
    }
    setLoading(true);
    const options = {
      category: "pending",
      date_range: formatAntdDateRange(range),
    };
    const result = await getTestsDataForPcr(searchGenistaId || "", options);
    if (result.success) {
      setPendingData(result.data);
      setPaginationConfig((prev) => ({ ...prev, total: result.data.length, current: 1 }));
    } else {
      toast.error("Something went wrong");
      setPendingData([]);
    }
    setLoading(false);
  }, []);

  // Called by PcrComponent via ref when switching to the Pending tab.
  const fetchPendingRecords = useCallback(() => {
    const range = defaultGbIdDateRange();
    setGenistaId("");
    setGbIdDateRange(range);
    setPendingData([]);
    loadPendingData("", range);
  }, [loadPendingData]);

  useImperativeHandle(ref, () => ({ fetchPendingRecords }), [fetchPendingRecords]);

  const handleDateRangeChange = useCallback((value) => {
    const range = value || defaultGbIdDateRange();
    setGbIdDateRange(range);
    loadPendingData(genistaId.trim(), range);
  }, [loadPendingData, genistaId]);

  const handleGenistaIdChange = useCallback((e) => {
    setGenistaId(e.target.value);
  }, []);

  const handleSearchClick = useCallback((value) => {
    loadPendingData((value ?? genistaId).trim());
  }, [genistaId, loadPendingData]);

  const toggleError = useCallback((error) => {
    setShowError(error);
  }, []);

  const tableDataSource = useMemo(() => pendingData.map((item, index) => ({
    ...item,
    key: `${item.WorkorderId}-${index}`,
  })), [pendingData]);

  return (
    <div className="PcrPending">
      <div className="PcrPending__searchRow">
        <div className="field-container">
          <label>Search</label>
          <Input.Search
            className="PcrGenistaSearch"
            placeholder="Input Genista ID"
            value={genistaId}
            onChange={handleGenistaIdChange}
            onSearch={handleSearchClick}
            allowClear
          />
        </div>
        <div className="field-container">
          <label>Workorder Date Range</label>
          <RangePicker
            className="PcrDateFilter"
            format="MM/DD/YYYY"
            value={gbIdDateRange}
            onChange={handleDateRangeChange}
            allowClear={false}
          />
        </div>
      </div>
      <Table
        sticky
        bordered
        className="PcrPendingTable"
        columns={tableColumns}
        dataSource={tableDataSource}
        scroll={{ y: "60vh" }}
        pagination={{ ...paginationConfig, onChange: handlePageChange }}
        loading={loading}
        rowClassName={() => "PcrPending__Table__Tr"}
      />
      <div className="PcrPending__Gap" />
      {showError.show && <Error message={showError.message} toggleError={toggleError} />}
    </div>
  );
});

PcrPendingQueue.displayName = "PcrPendingQueue";

export default PcrPendingQueue;
